import { create } from 'zustand';
import type { User } from '@/domain/model/User';
import { AuthService } from '@/application/service/AuthService';
import { AuthApiAdapter } from '@/infrastructure/api/AuthApiAdapter';
import { tokenStorage } from '@/infrastructure/storage/tokenStorage';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  redirectPath: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  checkAuth: () => Promise<void>;
  logout: () => void;
  setRedirectPath: (path: string) => void;
  clearRedirectPath: () => void;
  addPurchasedSubcategory: (subcategoryId: string) => void;
}

const authService = new AuthService(new AuthApiAdapter());

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  redirectPath: null,

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const response = await authService.login({ email, password });
      tokenStorage.setToken(response.token);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  signup: async (name, email, password) => {
    set({ isLoading: true });
    try {
      const response = await authService.signup({ name, email, password });
      tokenStorage.setToken(response.token);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  checkAuth: async () => {
    const token = tokenStorage.getToken();
    if (!token) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    set({ isLoading: true });
    try {
      const user = await authService.getCurrentUser();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      tokenStorage.removeToken();
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  logout: () => {
    tokenStorage.removeToken();
    set({ user: null, isAuthenticated: false, redirectPath: null });
  },

  setRedirectPath: (path) => set({ redirectPath: path }),

  clearRedirectPath: () => set({ redirectPath: null }),

  addPurchasedSubcategory: (subcategoryId) => {
    const { user } = get();
    if (!user) return;
    const purchasedSubcategories = user.purchasedSubcategories ?? [];
    if (purchasedSubcategories.includes(subcategoryId)) return;
    set({
      user: {
        ...user,
        purchasedSubcategories: [...purchasedSubcategories, subcategoryId],
      },
    });
  },
}));
